import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { CampaignStatusBadge } from '@/features/campaigns/components/CampaignStatusBadge';
import { templateLabel } from '@/features/campaigns/types';
import { InboundRowActions } from './components/InboundRowActions';
import type { InboundCampaignRow } from './types';

const num = (v: number | undefined): string =>
  v === null || v === undefined ? '—' : v.toLocaleString();

const text = (v: string | number | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

export function buildInboundColumns(opts?: {
  showOwner?: boolean;
  hideActions?: boolean;
}): ColumnDef<InboundCampaignRow>[] {
  const { showOwner = false, hideActions = false } = opts ?? {};

  const cols: ColumnDef<InboundCampaignRow>[] = [
    {
      accessorKey: 'campaignId',
      header: 'Campaign ID',
      size: 120,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.campaignId}
        </span>
      ),
    },
    {
      accessorKey: 'campaignName',
      header: 'Campaign',
      cell: ({ row }) => (
        <Link
          to={`/voice-campaign-info/${row.original.campaignId}`}
          className="font-medium text-foreground hover:underline"
        >
          {row.original.campaignName}
        </Link>
      ),
    },
  ];

  if (showOwner) {
    cols.push(
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId ?? '—'}
          </span>
        ),
      },
      { accessorKey: 'username', header: 'Owner', size: 160 },
    );
  }

  cols.push(
    {
      accessorKey: 'campaignStatus',
      header: 'Status',
      size: 140,
      cell: ({ row }) => <CampaignStatusBadge status={row.original.campaignStatus} />,
    },
    {
      accessorKey: 'templateId',
      header: 'Type',
      size: 140,
      cell: ({ row }) => templateLabel(row.original.templateId),
    },
    {
      accessorKey: 'did',
      header: 'DID',
      size: 160,
      cell: ({ row }) => (
        <span className="font-mono text-xs">{text(row.original.did)}</span>
      ),
    },
    {
      accessorKey: 'cliList',
      header: 'CLI',
      size: 160,
      cell: ({ row }) => (
        <span className="font-mono text-xs">{text(row.original.cliList)}</span>
      ),
    },
    {
      accessorKey: 'scheduleTime',
      header: 'Start',
      size: 180,
      cell: ({ row }) => text(row.original.scheduleTime),
    },
    {
      accessorKey: 'endTime',
      header: 'End',
      size: 180,
      cell: ({ row }) => text(row.original.endTime),
    },
    {
      accessorKey: 'numbersProcessed',
      header: 'Dialed',
      size: 110,
      cell: ({ row }) => num(row.original.numbersProcessed),
    },
    {
      accessorKey: 'callsConnected',
      header: 'Connected',
      size: 120,
      cell: ({ row }) => num(row.original.callsConnected),
    },
    {
      accessorKey: 'pulses',
      header: 'Pulses',
      size: 110,
      cell: ({ row }) => num(row.original.pulses),
    },
    {
      accessorKey: 'dtmfCount',
      header: 'DTMF',
      size: 100,
      cell: ({ row }) => num(row.original.dtmfCount),
    },
    {
      accessorKey: 'smsCount',
      header: 'SMS',
      size: 100,
      cell: ({ row }) => num(row.original.smsCount),
    },
    {
      accessorKey: 'retryCount',
      header: 'Retries',
      size: 110,
      cell: ({ row }) => num(row.original.retryCount),
    },
  );

  if (!hideActions) {
    cols.push({
      id: 'actions',
      header: 'Settings',
      size: 90,
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => <InboundRowActions campaign={row.original} />,
    });
  }

  return cols;
}

export const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = {
  campaignId: true,
  campaignName: true,
  campaignStatus: true,
  templateId: true,
  did: true,
  cliList: true,
  scheduleTime: true,
  endTime: true,
  numbersProcessed: true,
  callsConnected: true,
  pulses: true,
  // Niche counters off by default:
  dtmfCount: false,
  smsCount: false,
  retryCount: false,
};
